import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CustomerBalances.css'; // Updated CSS file (already contains gradient background)

/* ===============================
   Skeleton Loader Component
=============================== */
const SkeletonLoader = () => (
  <tr>
    <td><div className="skeleton skeleton-text"></div></td>
    <td><div className="skeleton skeleton-text"></div></td>
    <td><div className="skeleton skeleton-text"></div></td>
    <td><div className="skeleton skeleton-text"></div></td>
    <td><div className="skeleton skeleton-progress"></div></td>
    <td><div className="skeleton skeleton-text skeleton-text__short"></div></td>
  </tr>
);

function CustomerBalancesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const API_URL = 'https://installmentzone.com/api/admin_panel/v1/balances/balances.php';

    const timer = setTimeout(() => {
      axios
        .get(API_URL)
        .then(response => {
          if (response.data && response.data.success) {
            setBalances(response.data.data);
          } else {
            setError('Failed to fetch data. Please check the API response.');
          }
        })
        .catch(err => {
          console.error('Error fetching balances:', err);
          setError('Could not connect to the server. Please try again later.');
        })
        .finally(() => {
          setLoading(false);
        });
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const calculateProgress = (paid, total) => {
    if (total <= 0) return 0;
    const percentage = (paid / total) * 100;
    return Math.min(percentage, 100);
  };

  const filteredBalances = balances.filter(customer =>
    customer.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone_number.includes(searchTerm)
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "scroll",
        scrollBehavior: "smooth",
      }}
    >
      <div className="balance-container">
        <div className="header">
          <h1 className="header-title">Customer Device Balances</h1>
          <p className="header-subtitle">
            An overview of all active device payment plans.
          </p>
        </div>

        {/* Search Bar */}
        <div className="toolbar">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or phone number..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Table Section */}
        <div className="table-wrapper">
          <table className="balance-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Device</th>
                <th className="text-right">Total Price</th>
                <th className="text-right">Amount Paid</th>
                <th>Payment Progress</th>
                <th className="text-right">Balance Due</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => <SkeletonLoader key={i} />)
              ) : filteredBalances.length > 0 ? (
                filteredBalances.map((row, index) => {
                  const progress = calculateProgress(row.total_paid, row.total_price);
                  return (
                    <tr key={index}>
                      <td data-label="Customer">
                        <div className="customer-name">{row.full_name}</div>
                        <div className="customer-phone">{row.phone_number}</div>
                      </td>
                      <td data-label="Device">{row.device_name}</td>
                      <td data-label="Total Price" className="text-right currency">
                        GH₵ {parseFloat(row.total_price).toFixed(2)}
                      </td>
                      <td data-label="Amount Paid" className="text-right currency amount-paid">
                        GH₵ {parseFloat(row.total_paid).toFixed(2)}
                      </td>
                      <td data-label="Payment Progress">
                        <div className="progress-bar-container">
                          <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                        </div>
                        <span className="progress-text">{Math.round(progress)}%</span>
                      </td>
                      <td data-label="Balance Due" className="text-right currency balance-due">
                        GH₵ {parseFloat(row.balance).toFixed(2)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="no-data">
                    {balances.length > 0
                      ? "No customers match your search."
                      : "No customers with active device payments were found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CustomerBalancesPage;
